import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { rowHasSpeechBuddy } from '../helpers/speechBuddy';
import { Box, Typography, IconButton, Paper, Slider, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import Loader from './Loader';

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0973b9' : '#7ebc0a';
const iconButtonSx = {
    color,
    '&:hover': { backgroundColor: `${color}15` },
    '&.Mui-disabled': { color: '#ccc' },
};

const defaultPaperSx = {
    mt: 2,
    p: 1.5,
    borderRadius: 2,
    border: '1px solid #e5e8ed',
    bgcolor: '#fff',
};

export default function AudioPlayerBar({
    onPrev,
    onNext,
    onPlayPause,
    onSeek,
    onDownload,
    currentTrack,
    playingId,
    progress,
    duration,
    currentTime,
    volume,
    onVolumeChange,
    playerLabel,
    canGoPrev,
    canGoNext,
    formatTime,
    formatFileSize,
    formatFileDate,
    langIndex = 1,
    paperSx,
    onText,
    textLoading,
    speed = 1,
    onSpeedChange,
}) {
    const speechbuddy = useSelector((state) => state.history.speechbuddy);
    const completed = useSelector((state) => state.history.completed);
    const speechAllowed = !onText || rowHasSpeechBuddy(currentTrack, speechbuddy);
    const speechDone = !!(currentTrack?.isEmotion || completed?.find((item) => item.id === currentTrack?.linkedid));
    const SPEEDS = [1, 1.25, 1.5, 1.75, 2];
    const handleSpeedClick = useCallback(() => {
        if (!onSpeedChange) return;
        const idx = SPEEDS.indexOf(speed);
        const nextIdx = (idx + 1) % SPEEDS.length;
        onSpeedChange(SPEEDS[nextIdx]);
    }, [speed, onSpeedChange]);

    const prevTitle = langIndex === 2 ? 'Предыдущий' : langIndex === 3 ? 'Նախորդ' : 'Previous';
    const nextTitle = langIndex === 2 ? 'Следующий' : langIndex === 3 ? 'Հաջորդ' : 'Next';
    const downloadTitle = langIndex === 2 ? 'Скачать' : langIndex === 3 ? 'Ներբեռնել' : 'Download';
    const infoTitle = langIndex === 2 ? 'Информация' : langIndex === 3 ? 'Տեղեկություն' : 'Info';
    const nameLabel = langIndex === 2 ? 'Название' : langIndex === 3 ? 'Անուն' : 'Name';
    const sizeLabel = langIndex === 2 ? 'Размер' : langIndex === 3 ? 'Չափ' : 'Size';
    const dateLabel = langIndex === 2 ? 'Дата' : langIndex === 3 ? 'Ամսաթիվ' : 'Uploaded';

    const infoTooltipContent = currentTrack ? (
        <Box component="span" sx={{ display: 'block', p: 1.5, minWidth: 220 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography component="span" variant="caption" color="inherit" sx={{ opacity: 0.9 }}>
                    {nameLabel}
                </Typography>
                <Typography component="span" variant="caption" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>
                    {currentTrack?.name || currentTrack?.fname || currentTrack?.id || '—'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography component="span" variant="caption" color="inherit" sx={{ opacity: 0.9 }}>
                    ID
                </Typography>
                <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {currentTrack?.fname || currentTrack?.id || '—'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography component="span" variant="caption" color="inherit" sx={{ opacity: 0.9 }}>
                    {sizeLabel}
                </Typography>
                <Typography component="span" variant="caption">
                    {formatFileSize(currentTrack?.size)}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography component="span" variant="caption" color="inherit" sx={{ opacity: 0.9 }}>
                    {dateLabel}
                </Typography>
                <Typography component="span" variant="caption">
                    {currentTrack?.date ? formatFileDate(currentTrack.date) : (currentTrack?._addedAt ? formatFileDate(currentTrack._addedAt) : '—')}
                </Typography>
            </Box>
        </Box>
    ) : infoTitle;

    return (
        <Paper
            elevation={2}
            sx={{ ...defaultPaperSx, ...paperSx }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Tooltip title={prevTitle}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={onPrev}
                            disabled={!canGoPrev}
                            sx={iconButtonSx}
                            aria-label="Previous"
                        >
                            <SkipPreviousIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton
                    size="small"
                    onClick={onPlayPause}
                    disabled={!currentTrack}
                    sx={iconButtonSx}
                    aria-label={playingId ? 'Pause' : 'Play'}
                >
                    {playingId ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <Tooltip title={nextTitle}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={onNext}
                            disabled={!canGoNext}
                            sx={iconButtonSx}
                            aria-label="Next"
                        >
                            <SkipNextIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography variant="body2" sx={{ minWidth: 120, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {playerLabel}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 120, maxWidth: 400, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', width: 36 }}>{formatTime(currentTime)}</Typography>
                    <Slider
                        size="small"
                        value={progress}
                        onChange={onSeek}
                        disabled={!currentTrack || !duration}
                        sx={{
                            color,
                            '& .MuiSlider-thumb': { width: 14, height: 14 },
                            '& .MuiSlider-rail': { opacity: 0.5 },
                        }}
                        aria-label="Seek"
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', width: 36 }}>{formatTime(duration)}</Typography>
                </Box>
                {onSpeedChange ? (
                    <Tooltip title={langIndex === 2 ? 'Скорость' : langIndex === 3 ? 'Արագություն' : 'Speed'}>
                        <Typography
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={handleSpeedClick}
                            disabled={!currentTrack}
                            sx={{
                                minWidth: 36,
                                py: 0.5,
                                px: 1,
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                cursor: currentTrack ? 'pointer' : 'default',
                                color: currentTrack ? color : '#ccc',
                                backgroundColor: 'transparent',
                                '&:hover': currentTrack ? { backgroundColor: `${color}15` } : {},
                                fontFamily: 'inherit',
                            }}
                        >
                            {speed}x
                        </Typography>
                    </Tooltip>
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 100, maxWidth: 140 }}>
                    <VolumeUpIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                    <Slider
                        size="small"
                        value={Math.round(volume * 100)}
                        onChange={(_, value) => onVolumeChange(Number(value) / 100)}
                        min={0}
                        max={100}
                        sx={{
                            color,
                            '& .MuiSlider-thumb': { width: 12, height: 12 },
                            '& .MuiSlider-rail': { opacity: 0.5 },
                        }}
                        aria-label="Volume"
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <Tooltip title={currentTrack ? downloadTitle : ''}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => currentTrack && onDownload(currentTrack)}
                                disabled={!currentTrack}
                                sx={iconButtonSx}
                                aria-label="Download"
                            >
                                <DownloadIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {onText ? (
                        <Tooltip title={langIndex === 2 ? 'Транскрипция' : langIndex === 3 ? 'Տրանսկրիպցիա' : 'Transcription'}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => currentTrack && speechAllowed && onText(currentTrack)}
                                    disabled={!currentTrack || !speechAllowed}
                                    sx={{
                                        ...iconButtonSx,
                                        color: speechDone ? color : '#52658c',
                                        '&:hover': { backgroundColor: `${speechDone ? color : '#52658c'}15` },
                                    }}
                                    aria-label="Transcription"
                                >
                                    {textLoading ? <Loader /> : <ChatBubbleIcon sx={{ fontSize: 22 }} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                    <Tooltip
                        title={infoTooltipContent}
                        placement="left"
                        enterDelay={300}
                        leaveDelay={0}
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={!currentTrack}
                                sx={iconButtonSx}
                                aria-label="Info"
                            >
                                <InfoIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </Paper>
    );
}
